import Dexie from 'dexie';

export const db = new Dexie('ShopBook');

db.version(1).stores({
  products:      'id, business_id, name, updated_at, voided_at',
  sales:         'id, business_id, product_id, customer_id, payment_status, sold_at, updated_at, voided_at',
  sale_payments: 'id, business_id, sale_id, updated_at, voided_at',
  purchases:     'id, business_id, product_id, purchased_at, updated_at, voided_at',
  customers:     'id, business_id, name, contact, rating, updated_at, voided_at',
  outbox:        '++_localId, op_id, entity, op_type, synced_at',
  meta:          'key',
});

// v2 adds expenses for offline-capable expense tracking
db.version(2).stores({
  products:      'id, business_id, name, updated_at, voided_at',
  sales:         'id, business_id, product_id, customer_id, payment_status, sold_at, updated_at, voided_at',
  sale_payments: 'id, business_id, sale_id, updated_at, voided_at',
  purchases:     'id, business_id, product_id, purchased_at, updated_at, voided_at',
  customers:     'id, business_id, name, contact, rating, updated_at, voided_at',
  expenses:      'id, business_id, category, expense_at, updated_at, voided_at',
  outbox:        '++_localId, op_id, entity, op_type, synced_at',
  meta:          'key',
});

// v3 adds production_runs for bottling / assembly operations
db.version(3).stores({
  products:         'id, business_id, name, updated_at, voided_at',
  sales:            'id, business_id, product_id, customer_id, payment_status, sold_at, updated_at, voided_at',
  sale_payments:    'id, business_id, sale_id, updated_at, voided_at',
  purchases:        'id, business_id, product_id, purchased_at, updated_at, voided_at',
  customers:        'id, business_id, name, contact, rating, updated_at, voided_at',
  expenses:         'id, business_id, category, expense_at, updated_at, voided_at',
  production_runs:  'id, business_id, product_id, produced_at, updated_at, voided_at',
  outbox:           '++_localId, op_id, entity, op_type, synced_at',
  meta:             'key',
});

// Clear domain stores (not outbox/meta) before a full bootstrap pull.
export async function clearStores() {
  await Promise.all([
    db.products.clear(),
    db.sales.clear(),
    db.sale_payments.clear(),
    db.purchases.clear(),
    db.customers.clear(),
    db.expenses.clear(),
    db.production_runs.clear(),
  ]);
  await db.meta.put({ key: 'lastPull', value: null });
}

// Full wipe — domain stores AND the outbox AND the sync cursor. Used when the
// local DB must not carry anything from a previous account (account switch).
export async function resetLocalData() {
  await clearStores();
  await db.outbox.clear();
}

// Ensure the single shared local DB belongs to `accountKey`. The Dexie database
// is per-device, not per-account, so if a different account was last active we
// must wipe everything (including queued outbox ops) and force a fresh bootstrap
// — otherwise the new account would see, and even push, the old account's data.
// Returns true if a reset happened.
export async function ensureAccount(accountKey) {
  if (!accountKey) return false;
  const prev = await db.meta.get('account');
  if (prev?.value === accountKey) return false;
  await resetLocalData();
  await db.meta.put({ key: 'account', value: accountKey });
  return true;
}

// Reload when another tab opens a higher DB version — prevents DatabaseClosedError
db.on('versionchange', () => {
  db.close();
  window.location.reload();
});

export default db;
